/**
 * Admin Dashboard Component with Server-Side Authentication
 * Provides order management interface for authenticated admins
 * 
 * @module components/AdminDashboard
 * @requires react
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import './AdminDashboard.css';

/**
 * AdminDashboard component for managing orders with JWT authentication
 * @returns {JSX.Element} Admin dashboard with order management
 */
const AdminDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  // ── Soil Requests state ─────────────────────────────
  const [soilRequests, setSoilRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqError, setReqError] = useState('');
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);

  // Configure axios to include JWT token in all requests
  const setupAxiosInterceptor = (token) => {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  // Remove axios authorization header
  const removeAxiosInterceptor = () => {
    delete api.defaults.headers.common['Authorization'];
  };

  // Check if user is already authenticated when component loads
  useEffect(() => {
    const checkExistingAuth = async () => {
      const token = localStorage.getItem('adminToken');
      
      if (token) {
        try {
          setupAxiosInterceptor(token);
          
          // Verify token is still valid
          const response = await api.post('/api/auth/verify-token');
          
          if (response.data.success) {
            setIsAuthenticated(true);
            setCurrentAdmin(response.data.admin);
            setLoading(false);
          } else {
            // Token is invalid, remove it
            localStorage.removeItem('adminToken');
            removeAxiosInterceptor();
            setLoading(false);
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          localStorage.removeItem('adminToken');
          removeAxiosInterceptor();
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    checkExistingAuth();
  }, []);

  // Handle login form submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');

    try {
      const response = await api.post('/api/auth/login', {
        username: username.toLowerCase().trim(),
        password: password
      });

      if (response.data.success) {
        const { token, admin } = response.data;
        
        // Store token in localStorage
        localStorage.setItem('adminToken', token);
        
        // Setup axios for future requests
        setupAxiosInterceptor(token);
        
        // Update state
        setIsAuthenticated(true);
        setCurrentAdmin(admin);
        setAuthError('');
        setUsername('');
        setPassword('');
        
        console.log('✅ Login successful:', admin.username);
      }
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.response?.data?.message) {
        setAuthError(error.response.data.message);
      } else {
        setAuthError('Login failed. Please check your connection and try again.');
      }
      
      setPassword(''); // Clear password on error
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      // Optional: for logging; ok if it fails
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout endpoint error:', error);
    }
  
    // Clear local state and storage
    localStorage.removeItem('adminToken');
    removeAxiosInterceptor();
    setIsAuthenticated(false);
    setCurrentAdmin(null);
    setUsername('');
    setPassword('');
    setOrders([]);
    setStats({});
  }, []);

  // Fetch orders from API
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/api/admin/orders';
      const params = new URLSearchParams();
      
      if (statusFilter) params.append('status', statusFilter);
      if (dateFilter) params.append('date', dateFilter);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url);
      setOrders(response.data.orders);
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      
      if (err.response?.status === 401) {
        // Token expired or invalid
        setAuthError('Session expired. Please log in again.');
        handleLogout();
      } else {
        setError('Error loading orders. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFilter, handleLogout]); // deps that affect the fetch;

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/stats');
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
      
      if (err.response?.status === 401) {
        setAuthError('Session expired. Please log in again.');
        handleLogout();
      }
    }
  }, [handleLogout]);

    // ── Fetch Soil Requests (admin-protected) ──────────
  const fetchSoilRequests = useCallback(async (page = 1, limit = 50) => {
    try {
      setReqLoading(true);
      setReqError('');
      const res = await api.get('/api/requests', {
        params: { page, limit, sort: '-createdAt' }
      });
      setSoilRequests(res.data.items || []);
    } catch (err) {
      console.error('Error fetching soil requests:', err);
      if (err.response?.status === 401) {
        setAuthError('Session expired. Please log in again.');
        handleLogout();
      } else {
        setReqError('Error loading soil requests. Please try again.');
      }
    } finally {
      setReqLoading(false);
    }
  }, [handleLogout]);

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingOrderId(orderId);
      await api.put(`/api/admin/orders/${orderId}/status`, {
        status: newStatus
      });
      
      // Refresh orders after update
      await fetchOrders();
      await fetchStats();
    } catch (err) {
      console.error('Error updating order status:', err);
      
      if (err.response?.status === 401) {
        setAuthError('Session expired. Please log in again.');
        handleLogout();
      } else {
        setError('Error updating order status. Please try again.');
      }
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Load data when component mounts or filters change
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchOrders();
  }, [isAuthenticated, fetchOrders]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchStats();
  }, [isAuthenticated, fetchStats]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchSoilRequests(); // default page 1, limit 50
  }, [isAuthenticated, fetchSoilRequests]);

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-badge pending';
      case 'processing': return 'status-badge processing';
      case 'shipped': return 'status-badge shipped';
      case 'delivered': return 'status-badge delivered';
      case 'cancelled': return 'status-badge cancelled';
      default: return 'status-badge';
    }
  };

  // Show loading screen while checking authentication
  if (loading && !isAuthenticated) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Checking authentication...</div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-dashboard">
        <div className="auth-container">
          <div className="auth-form">
            <h1>Admin Login</h1>
            <p>Enter your admin credentials to access the dashboard</p>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="username">Username:</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                  disabled={authLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={authLoading}
                />
              </div>
              
              {authError && (
                <div className="auth-error">
                  {authError}
                </div>
              )}
              
              <button 
                type="submit" 
                className="auth-submit"
                disabled={authLoading}
              >
                {authLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
            
            <div className="auth-hint">
              <small>Valid usernames: tyler, griffin</small>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show main dashboard if authenticated
  if (loading && orders.length === 0) {
    return <div className="admin-dashboard loading">Loading dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <div className="header-title">
          <h1>Admin Dashboard</h1>
          <p>Welcome back, {currentAdmin?.username}! Manage your SoiledIt orders and track business metrics</p>
        </div>
        <button onClick={handleLogout} className="logout-button">
          Logout
        </button>
      </header>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Orders</h3>
          <div className="stat-number">{stats.totalOrders || 0}</div>
        </div>
        <div className="stat-card pending">
          <h3>Pending Orders</h3>
          <div className="stat-number">{stats.pendingOrders || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Today's Orders</h3>
          <div className="stat-number">{stats.todayOrders || 0}</div>
        </div>
        <div className="stat-card revenue">
          <h3>Total Revenue</h3>
          <div className="stat-number">{formatCurrency(stats.totalRevenue || 0)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label htmlFor="status-filter">Filter by Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="date-filter">Filter by Date:</label>
          <select
            id="date-filter"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>

        <button onClick={fetchOrders} className="refresh-button">
          Refresh Orders
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Orders Table */}
      <div className="orders-section">
        <h2>Orders ({orders.length})</h2>
        
        {orders.length === 0 ? (
          <div className="no-orders">
            <p>No orders found with the current filters.</p>
          </div>
        ) : (
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order._id}>
                    <td>
                      <span className="order-number">#{order.orderNumber}</span>
                    </td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      <div className="customer-info">
                        <div className="customer-name">{order.customer.name}</div>
                        <div className="customer-address">
                          {order.shippingAddress.city}, {order.shippingAddress.state}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="order-items">
                        {order.items.map((item, index) => (
                          <div key={index} className="order-item">
                            <span className="item-name">{item.soilMixName}</span>
                            <span className="item-quantity">Qty: {item.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="order-total">{formatCurrency(order.totalAmount)}</td>
                    <td>
                      <span className={getStatusBadgeClass(order.status)}>
                        {order.status}
                      </span>
                    </td>
                    <td>
                      <div className="order-actions">
                        <select
                          value={order.status}
                          onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                          disabled={updatingOrderId === order._id}
                          className="status-select"
                        >
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        
                        {order.stripeLink && (
                          <a 
                            href={order.stripeLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="stripe-link"
                          >
                            View in Stripe
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <h2>Soil Mix Requests ({soilRequests.length})</h2>

        <div style={{ display: 'flex', gap: '.5rem', marginBottom: '0.75rem' }}>
          <button onClick={() => fetchSoilRequests()} className="refresh-button">
            Refresh Requests
          </button>
        </div>

        {reqLoading ? (
          <div className="loading-message">Loading requests...</div>
        ) : reqError ? (
          <div className="error-message">{reqError}</div>
        ) : soilRequests.length === 0 ? (
          <div className="no-orders"><p>No requests found.</p></div>
        ) : (
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Plant</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {soilRequests.map((r) => (
                  <tr key={r._id}>
                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                    <td>{r.name}</td>
                    <td>{r.email}</td>
                    <td>{r.plant}</td>
                    <td style={{ whiteSpace: 'pre-wrap', maxWidth: 400 }}>{r.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;